import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import {
  UserGroupIcon,
  ArrowTrendingUpIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BoltIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  FunnelIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const PERIODOS = [
  { label: '7d', value: 7 },
  { label: '15d', value: 15 },
  { label: '30d', value: 30 },
  { label: '60d', value: 60 },
  { label: '90d', value: 90 },
]

function VariacaoIndicator({ valor }) {
  if (valor === undefined || valor === null) return null
  const positivo = valor >= 0
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
        positivo ? 'text-emerald-600' : 'text-red-500'
      }`}
    >
      {positivo ? (
        <ArrowUpIcon className="w-3 h-3" />
      ) : (
        <ArrowDownIcon className="w-3 h-3" />
      )}
      {Math.abs(Number(valor)).toFixed(1)}%
    </span>
  )
}

function KPICard({ icon: Icon, label, value, variacao, gradient, iconBg, sublabel }) {
  return (
    <div
      className={`rounded-2xl p-5 shadow-sm border border-white/60 ${gradient} relative overflow-hidden`}
    >
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-white/10 -translate-y-8 translate-x-8 pointer-events-none" />
      <div className="relative z-10">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${iconBg}`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="text-2xl font-bold text-gray-800 leading-tight">
          {value !== undefined && value !== null ? value : '—'}
        </div>
        <div className="text-sm font-medium text-gray-600 mt-0.5">{label}</div>
        {(variacao !== undefined || sublabel) && (
          <div className="mt-2 flex items-center gap-2">
            {variacao !== undefined && <VariacaoIndicator valor={variacao} />}
            {sublabel && <span className="text-xs text-gray-500">{sublabel}</span>}
          </div>
        )}
      </div>
    </div>
  )
}

function ProgressBar({ label, atual, meta, percentual, color }) {
  const pct = Math.min(100, Math.max(0, Number(percentual) || 0))
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {atual} / {meta}
          </span>
          <span
            className={`text-xs font-bold ${
              pct >= 100
                ? 'text-emerald-600'
                : pct >= 70
                ? 'text-blue-600'
                : 'text-orange-500'
            }`}
          >
            {pct.toFixed(0)}%
          </span>
        </div>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-700 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function BarChart({ data, valueKey = 'total', colorClass = 'bg-blue-500', height = 80 }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-gray-400 text-sm py-8">Sem dados</div>
    )
  }
  const maxVal = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1)
  return (
    <div className="flex items-end gap-0.5 w-full" style={{ height }}>
      {data.map((item, i) => {
        const val = Number(item[valueKey]) || 0
        const pct = (val / maxVal) * 100
        return (
          <div
            key={i}
            className="flex-1 flex flex-col items-center gap-1 group relative"
          >
            <div
              className={`w-full rounded-t-sm ${colorClass} transition-all duration-300 group-hover:opacity-75`}
              style={{ height: `${Math.max(pct, 2)}%` }}
            />
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              {val}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function HorizontalBarList({
  data,
  valueKey = 'total',
  labelKey,
  barColor = '#3b82f6',
  showConvertidos = false,
}) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-gray-400 text-sm py-4">Sem dados</div>
    )
  }
  const maxVal = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1)
  return (
    <div className="space-y-3">
      {data.slice(0, 8).map((item, i) => {
        const val = Number(item[valueKey]) || 0
        const pct = (val / maxVal) * 100
        return (
          <div key={i}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-700 truncate max-w-[140px]">
                {item[labelKey] || '—'}
              </span>
              <div className="flex items-center gap-2">
                {showConvertidos && item.convertidos !== undefined && (
                  <span className="text-xs text-emerald-600">
                    {item.convertidos} conv.
                  </span>
                )}
                <span className="text-xs font-bold text-gray-800">{val}</span>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, backgroundColor: barColor }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function FunnelChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-gray-400 text-sm py-8">Sem dados</div>
    )
  }
  const maxVal = Math.max(...data.map((d) => Number(d.total) || 0), 1)
  return (
    <div className="space-y-2.5">
      {data.map((stage, i) => {
        const val = Number(stage.total) || 0
        const pct = (val / maxVal) * 100
        const valor = Number(stage.valor || 0)
        const color = stage.color || '#3b82f6'
        return (
          <div key={i}>
            <div className="flex items-center gap-3 mb-0.5">
              <span className="text-xs font-semibold text-gray-600 w-32 truncate shrink-0">
                {stage.name}
              </span>
              <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                <div
                  className="h-full rounded-full flex items-center px-2 transition-all duration-700"
                  style={{
                    width: `${Math.max(pct, 5)}%`,
                    backgroundColor: color,
                  }}
                >
                  {val > 0 && (
                    <span className="text-white text-xs font-bold whitespace-nowrap">
                      {val}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right min-w-[90px] shrink-0">
                <span className="text-xs font-bold text-gray-800 block">
                  {val} leads
                </span>
                {valor > 0 && (
                  <span className="text-xs text-gray-500">
                    R$ {(valor / 1000).toFixed(0)}k
                  </span>
                )}
              </div>
            </div>
            {stage.taxa_conversao !== undefined && (
              <div className="pl-[136px] text-xs text-gray-400 leading-tight">
                Conversao: {Number(stage.taxa_conversao).toFixed(1)}%
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ScoreBar({ score }) {
  const pct = Math.min(100, Math.max(0, Number(score) || 0))
  const color =
    pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-yellow-400' : 'bg-red-400'
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 bg-gray-100 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-bold text-gray-700">{pct}</span>
    </div>
  )
}

function StageBadge({ name, color }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: color || '#6b7280' }}
    >
      {name || 'N/A'}
    </span>
  )
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}
      >
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-lg font-bold text-gray-800 leading-tight">
          {value !== undefined && value !== null ? value : '—'}
        </div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  )
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  } catch {
    return String(dateStr)
  }
}

function formatDayLabel(dateStr) {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    return `${d.getDate()}/${d.getMonth() + 1}`
  } catch {
    return String(dateStr)
  }
}

function formatCurrency(val) {
  const n = Number(val) || 0
  if (n >= 1000000) return `R$ ${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(0)}k`
  return `R$ ${n.toFixed(0)}`
}

const META_LABELS = {
  leads_mes: 'Leads no Mes',
  conversao_mes: 'Conversoes no Mes',
  whatsapp_mes: 'WhatsApp Enviados',
  email_mes: 'E-mails Enviados',
}

const META_COLORS = {
  leads_mes: 'bg-blue-500',
  conversao_mes: 'bg-emerald-500',
  whatsapp_mes: 'bg-green-500',
  email_mes: 'bg-orange-500',
}

export default function MarketingDashboardPage() {
  const [periodo, setPeriodo] = useState(30)
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState(null)
  const [leadsPorDia, setLeadsPorDia] = useState([])
  const [leadsPorOrigem, setLeadsPorOrigem] = useState([])
  const [leadsPorSetor, setLeadsPorSetor] = useState([])
  const [funil, setFunil] = useState([])
  const [comunicacoesPorDia, setComunicacoesPorDia] = useState({
    whatsapp: [],
    email: [],
  })
  const [topLeads, setTopLeads] = useState([])
  const [metas, setMetas] = useState([])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [
        ovRes,
        ldRes,
        loRes,
        lsRes,
        funilRes,
        comRes,
        tlRes,
        metRes,
      ] = await Promise.allSettled([
        api.get('/api/marketing/dashboard/overview'),
        api.get(`/api/marketing/dashboard/leads-por-dia?dias=${periodo}`),
        api.get(`/api/marketing/dashboard/leads-por-origem?dias=${periodo}`),
        api.get(`/api/marketing/dashboard/leads-por-setor?dias=${periodo}`),
        api.get('/api/marketing/dashboard/funil'),
        api.get(
          `/api/marketing/dashboard/comunicacoes-por-dia?dias=${periodo}`
        ),
        api.get('/api/marketing/dashboard/top-leads?limite=10'),
        api.get('/api/marketing/dashboard/metas'),
      ])

      if (ovRes.status === 'fulfilled') setOverview(ovRes.value.data)
      if (ldRes.status === 'fulfilled')
        setLeadsPorDia(ldRes.value.data || [])
      if (loRes.status === 'fulfilled')
        setLeadsPorOrigem(loRes.value.data || [])
      if (lsRes.status === 'fulfilled')
        setLeadsPorSetor(lsRes.value.data || [])
      if (funilRes.status === 'fulfilled') setFunil(funilRes.value.data || [])
      if (comRes.status === 'fulfilled')
        setComunicacoesPorDia(
          comRes.value.data || { whatsapp: [], email: [] }
        )
      if (tlRes.status === 'fulfilled') setTopLeads(tlRes.value.data || [])
      if (metRes.status === 'fulfilled') setMetas(metRes.value.data || [])
    } catch (err) {
      toast.error('Erro ao carregar dados do dashboard')
    } finally {
      setLoading(false)
    }
  }, [periodo])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const leads = overview?.leads || {}
  const whatsapp = overview?.whatsapp || {}
  const emailStats = overview?.email || {}
  const automacoes = overview?.automacoes || {}
  const calendario = overview?.calendario || {}

  const leadsPorDiaFormatado = leadsPorDia.map((d) => ({
    ...d,
    _label: formatDayLabel(d.date),
  }))

  const whatsappPorDia = (comunicacoesPorDia.whatsapp || []).reduce(
    (acc, item) => {
      const existing = acc.find((a) => a.date === item.date)
      if (existing) {
        existing.total = (existing.total || 0) + (Number(item.total) || 0)
      } else {
        acc.push({ date: item.date, total: Number(item.total) || 0 })
      }
      return acc
    },
    []
  )

  const emailPorDia = comunicacoesPorDia.email || []

  const leadsPorOrigemComCores = leadsPorOrigem.map((item, i) => {
    const cores = [
      '#8b5cf6',
      '#6366f1',
      '#3b82f6',
      '#0ea5e9',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#ec4899',
    ]
    return { ...item, _barColor: cores[i % cores.length] }
  })

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ChartBarIcon className="w-7 h-7 text-blue-600" />
            Dashboard Marketing
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            CRM B2B — Visao Unificada de Performance
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 mr-1">Periodo:</span>
          {PERIODOS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriodo(p.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                periodo === p.value
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-9 h-9 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <span className="ml-3 text-gray-500 text-sm">Carregando dashboard...</span>
        </div>
      ) : (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <KPICard
              icon={UserGroupIcon}
              label="Total Leads"
              value={leads.total ?? 0}
              variacao={leads.variacao}
              gradient="bg-gradient-to-br from-blue-50 to-blue-100"
              iconBg="bg-blue-600"
              sublabel={`${leads.ativos ?? 0} ativos`}
            />
            <KPICard
              icon={ArrowTrendingUpIcon}
              label="Conversoes"
              value={leads.convertidos ?? 0}
              variacao={leads.variacao_conversao}
              gradient="bg-gradient-to-br from-emerald-50 to-emerald-100"
              iconBg="bg-emerald-600"
              sublabel={
                leads.total > 0
                  ? `${(
                      ((leads.convertidos || 0) / leads.total) *
                      100
                    ).toFixed(1)}% taxa`
                  : undefined
              }
            />
            <KPICard
              icon={StarIcon}
              label="Score Medio"
              value={
                leads.score_medio !== undefined
                  ? Number(leads.score_medio).toFixed(0)
                  : '—'
              }
              gradient="bg-gradient-to-br from-amber-50 to-amber-100"
              iconBg="bg-amber-500"
              sublabel="de 100 pontos"
            />
            <KPICard
              icon={ChatBubbleLeftRightIcon}
              label="WhatsApp Env."
              value={whatsapp.enviadas ?? 0}
              gradient="bg-gradient-to-br from-green-50 to-green-100"
              iconBg="bg-green-600"
              sublabel={
                whatsapp.taxa_entrega !== undefined
                  ? `${Number(whatsapp.taxa_entrega).toFixed(0)}% entregues`
                  : undefined
              }
            />
            <KPICard
              icon={EnvelopeIcon}
              label="E-mails Enviados"
              value={emailStats.enviados ?? 0}
              gradient="bg-gradient-to-br from-orange-50 to-orange-100"
              iconBg="bg-orange-500"
              sublabel={
                emailStats.taxa_abertura !== undefined
                  ? `${Number(emailStats.taxa_abertura).toFixed(0)}% abertos`
                  : undefined
              }
            />
            <KPICard
              icon={CalendarDaysIcon}
              label="Posts Agendados"
              value={calendario.agendados ?? 0}
              gradient="bg-gradient-to-br from-purple-50 to-purple-100"
              iconBg="bg-purple-600"
              sublabel="proximos eventos"
            />
          </div>

          {/* ── Pipeline Funnel + Metas ── */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
            {/* Funil */}
            <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <FunnelIcon className="w-5 h-5 text-blue-600" />
                <h2 className="text-base font-semibold text-gray-800">
                  Pipeline de Vendas
                </h2>
                <span className="ml-auto text-xs text-gray-400">
                  por estagio
                </span>
              </div>
              <FunnelChart data={funil} />
              {funil.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-50 flex flex-wrap gap-2">
                  {funil.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-xs text-gray-600">{s.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Metas do Mes */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                <h2 className="text-base font-semibold text-gray-800">
                  Metas do Mes
                </h2>
              </div>
              {metas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                  <CheckCircleIcon className="w-10 h-10 mb-2 opacity-30" />
                  <span className="text-sm">Nenhuma meta cadastrada</span>
                </div>
              ) : (
                metas.map((meta, i) => (
                  <ProgressBar
                    key={i}
                    label={META_LABELS[meta.tipo] || meta.tipo}
                    atual={meta.valor_atual}
                    meta={meta.valor_meta}
                    percentual={meta.percentual}
                    color={META_COLORS[meta.tipo] || 'bg-blue-500'}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Leads por Dia + Origem + Setor ── */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
            {/* Leads por Dia */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <ChartBarIcon className="w-5 h-5 text-blue-600" />
                <h2 className="text-base font-semibold text-gray-800">
                  Leads por Dia
                </h2>
                <span className="ml-auto text-xs text-gray-400">
                  ultimos {periodo} dias
                </span>
              </div>
              <BarChart
                data={leadsPorDiaFormatado}
                valueKey="total"
                colorClass="bg-blue-500 hover:bg-blue-600"
                height={120}
              />
              {leadsPorDia.length > 1 && (
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-400">
                    {formatDate(leadsPorDia[0]?.date)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatDate(leadsPorDia[leadsPorDia.length - 1]?.date)}
                  </span>
                </div>
              )}
              {leadsPorDia.some((d) => Number(d.convertidos) > 0) && (
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <p className="text-xs text-gray-500 mb-2">
                    Convertidos por dia
                  </p>
                  <BarChart
                    data={leadsPorDiaFormatado}
                    valueKey="convertidos"
                    colorClass="bg-emerald-400"
                    height={60}
                  />
                </div>
              )}
            </div>

            {/* Origem + Setor */}
            <div className="flex flex-col gap-4">
              {/* Por Origem */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <SparklesIcon className="w-4 h-4 text-purple-600" />
                  <h2 className="text-sm font-semibold text-gray-800">
                    Leads por Origem
                  </h2>
                </div>
                {leadsPorOrigem.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-4">
                    Sem dados de origem
                  </div>
                ) : (
                  leadsPorOrigemComCores.map((item, i) => {
                    const val = Number(item.total) || 0
                    const maxVal = Math.max(
                      ...leadsPorOrigemComCores.map(
                        (d) => Number(d.total) || 0
                      ),
                      1
                    )
                    const pct = (val / maxVal) * 100
                    return (
                      <div key={i} className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700 truncate max-w-[140px]">
                            {item.source || '—'}
                          </span>
                          <div className="flex items-center gap-2">
                            {item.convertidos !== undefined && (
                              <span className="text-xs text-emerald-600">
                                {item.convertidos} conv.
                              </span>
                            )}
                            <span className="text-xs font-bold text-gray-800">
                              {val}
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: item._barColor,
                            }}
                          />
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Por Setor */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <BuildingOfficeIcon className="w-4 h-4 text-orange-600" />
                  <h2 className="text-sm font-semibold text-gray-800">
                    Leads por Setor
                  </h2>
                </div>
                {leadsPorSetor.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-4">
                    Sem dados de setor
                  </div>
                ) : (
                  leadsPorSetor.map((item, i) => {
                    const val = Number(item.total) || 0
                    const maxVal = Math.max(
                      ...leadsPorSetor.map((d) => Number(d.total) || 0),
                      1
                    )
                    const pct = (val / maxVal) * 100
                    return (
                      <div key={i} className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700 truncate max-w-[140px]">
                            {item.sector || '—'}
                          </span>
                          <div className="flex items-center gap-2">
                            {item.convertidos !== undefined && (
                              <span className="text-xs text-emerald-600">
                                {item.convertidos} conv.
                              </span>
                            )}
                            <span className="text-xs font-bold text-gray-800">
                              {val}
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-orange-500 transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>

          {/* ── Comunicacoes ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* WhatsApp */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-green-600 rounded-xl flex items-center justify-center shrink-0">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-800">
                    WhatsApp
                  </h2>
                  <p className="text-xs text-gray-400">
                    ultimos {periodo} dias
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xl font-bold text-green-600">
                    {whatsapp.total ?? 0}
                  </div>
                  <div className="text-xs text-gray-400">total msgs</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <StatCard
                  label="Enviadas"
                  value={whatsapp.enviadas}
                  icon={ArrowUpIcon}
                  color="bg-green-600"
                />
                <StatCard
                  label="Recebidas"
                  value={whatsapp.recebidas}
                  icon={ArrowDownIcon}
                  color="bg-blue-600"
                />
                <StatCard
                  label="Entregues"
                  value={whatsapp.entregues}
                  icon={CheckCircleIcon}
                  color="bg-emerald-600"
                />
                <StatCard
                  label="Lidas"
                  value={whatsapp.lidas}
                  icon={StarIcon}
                  color="bg-amber-500"
                />
              </div>
              {whatsapp.taxa_entrega !== undefined && (
                <div className="bg-green-50 rounded-xl p-3 flex items-center justify-between">
                  <span className="text-sm text-gray-600 font-medium">
                    Taxa de Entrega
                  </span>
                  <span className="text-xl font-bold text-green-700">
                    {Number(whatsapp.taxa_entrega).toFixed(1)}%
                  </span>
                </div>
              )}
              {whatsappPorDia.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-gray-400 mb-2">
                    Mensagens por dia
                  </p>
                  <BarChart
                    data={whatsappPorDia}
                    valueKey="total"
                    colorClass="bg-green-400"
                    height={50}
                  />
                </div>
              )}
            </div>

            {/* Email */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center shrink-0">
                  <EnvelopeIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-800">
                    E-mail Marketing
                  </h2>
                  <p className="text-xs text-gray-400">
                    ultimos {periodo} dias
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xl font-bold text-orange-600">
                    {emailStats.total ?? 0}
                  </div>
                  <div className="text-xs text-gray-400">total emails</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <StatCard
                  label="Enviados"
                  value={emailStats.enviados}
                  icon={ArrowUpIcon}
                  color="bg-orange-500"
                />
                <StatCard
                  label="Entregues"
                  value={emailStats.entregues}
                  icon={CheckCircleIcon}
                  color="bg-emerald-600"
                />
                <StatCard
                  label="Abertos"
                  value={emailStats.abertos}
                  icon={StarIcon}
                  color="bg-blue-600"
                />
                <StatCard
                  label="Clicados"
                  value={emailStats.clicados}
                  icon={ArrowTrendingUpIcon}
                  color="bg-purple-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {emailStats.taxa_abertura !== undefined && (
                  <div className="bg-orange-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-orange-700">
                      {Number(emailStats.taxa_abertura).toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Taxa Abertura
                    </div>
                  </div>
                )}
                {emailStats.taxa_click !== undefined && (
                  <div className="bg-purple-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-purple-700">
                      {Number(emailStats.taxa_click).toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Taxa de Clique
                    </div>
                  </div>
                )}
              </div>
              {emailPorDia.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-gray-400 mb-2">
                    E-mails por dia
                  </p>
                  <BarChart
                    data={emailPorDia}
                    valueKey="total"
                    colorClass="bg-orange-400"
                    height={50}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ── Top Leads + Automacoes ── */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mb-6">
            {/* Top Leads Table */}
            <div className="xl:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <ArrowTrendingUpIcon className="w-5 h-5 text-blue-600" />
                <h2 className="text-base font-semibold text-gray-800">
                  Top 10 Leads
                </h2>
                <span className="ml-auto text-xs text-gray-400">
                  por valor estimado
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Empresa
                      </th>
                      <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Setor
                      </th>
                      <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Estagio
                      </th>
                      <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="text-right pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Valor Est.
                      </th>
                      <th className="text-right pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Ult. Contato
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topLeads.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-10 text-center text-gray-400 text-sm"
                        >
                          Nenhum lead encontrado
                        </td>
                      </tr>
                    ) : (
                      topLeads.map((lead, i) => (
                        <tr
                          key={lead.id || i}
                          className="hover:bg-gray-50/60 transition-colors"
                        >
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                                <span className="text-xs font-bold text-blue-700">
                                  {(lead.company_name || '?')
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-800 truncate max-w-[120px]">
                                  {lead.company_name || 'N/A'}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {lead.total_contatos ?? 0} contatos
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-3 hidden sm:table-cell">
                            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-lg">
                              {lead.sector || '—'}
                            </span>
                          </td>
                          <td className="py-3 pr-3">
                            <StageBadge
                              name={lead.stage_name}
                              color={lead.stage_color}
                            />
                          </td>
                          <td className="py-3 pr-3">
                            <ScoreBar score={lead.score} />
                          </td>
                          <td className="py-3 text-right font-semibold text-gray-800">
                            {lead.estimated_value
                              ? formatCurrency(lead.estimated_value)
                              : '—'}
                          </td>
                          <td className="py-3 text-right hidden md:table-cell">
                            <div className="flex items-center justify-end gap-1 text-gray-500">
                              <ClockIcon className="w-3 h-3" />
                              <span className="text-xs">
                                {formatDate(lead.last_contact_at)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Automacoes */}
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl shadow-sm p-5 text-white flex flex-col">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                  <BoltIcon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-base font-semibold">Automacoes</h2>
              </div>
              <div className="space-y-3 flex-1">
                <div className="bg-white/15 rounded-xl p-4 text-center">
                  <div className="text-4xl font-bold">
                    {automacoes.ativas ?? 0}
                  </div>
                  <div className="text-xs text-purple-200 mt-1">
                    Automacoes Ativas
                  </div>
                </div>
                <div className="bg-white/15 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold">
                    {automacoes.execucoes_total ?? 0}
                  </div>
                  <div className="text-xs text-purple-200 mt-1">
                    Execucoes Totais
                  </div>
                </div>
                <div className="bg-white/15 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-300">
                    {automacoes.conversoes_total ?? 0}
                  </div>
                  <div className="text-xs text-purple-200 mt-1">
                    Conversoes Geradas
                  </div>
                </div>
              </div>
              <div className="pt-4 mt-3 border-t border-white/20 text-center">
                <span className="text-xs text-purple-200">
                  {automacoes.total ?? 0} automacoes cadastradas
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-gray-400 pb-4">
            Dashboard Marketing VIPLOC — Dados referentes aos ultimos {periodo}{' '}
            dias
          </div>
        </>
      )}
    </div>
  )
}
